import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Stack, Sheet, Button, Typography, styled } from "@financeable-com-au/financeable-ui";
import { captureMessage } from "@sentry/react";
import { usePostHog } from "posthog-js/react";
import { Loader } from "@components/Loader";
import { useLodgeAIAction } from "@/hooks/useLodgeAI";
import { lodgeAIAutomationSelector } from "@/store/slices/lodgeAISlice";
import { isActiveSessionState } from "@/providers/LodgeAIContextProvider/utils/sessionStateTypeGuards";
import { getModalFrameColour } from "@/components/LodgeAIModal/utils/modalAbstraction";

const STREAM_URL_TIMEOUT_MS = 40_000;

const StyledIFrame = styled("iframe")`
  width: 100%;
  min-height: 768px;
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  pointer-events: all;
  cursor: pointer;
`;

const AutomationFrame = () => {
  const [hasLoadingTimedOut, setHasLoadingTimedOut] = useState(false);
  const posthog = usePostHog();
  const { stopSession } = useLodgeAIAction();
  const lodgeAIAutomation = useSelector(lodgeAIAutomationSelector);

  const isInAutomation = useMemo(() => {
    return !lodgeAIAutomation?.isManualIntervention && !lodgeAIAutomation?.isCompleted;
  }, [lodgeAIAutomation?.isManualIntervention, lodgeAIAutomation?.isCompleted]);

  const streamUrl = useMemo(() => {
    if (isActiveSessionState(lodgeAIAutomation)) {
      return lodgeAIAutomation.streamUrl;
    }
    return undefined;
  }, [lodgeAIAutomation]);

  const handleClose = () => {
    const message = "LodgeAI: Stream URL timed out, user has closed automation";
    // Capture message in Sentry.
    captureMessage(message);
    // Capture message in Posthog.
    posthog?.capture(message);
    // Stop session (and close modal).
    stopSession();
  };

  useEffect(() => {
    if (streamUrl) {
      setHasLoadingTimedOut(false);
      return;
    }

    const timer = setTimeout(() => {
      setHasLoadingTimedOut(true);
    }, STREAM_URL_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [streamUrl]);

  return (
    <Sheet
      borderRadius="lg"
      borderWidth="md"
      color={getModalFrameColour(lodgeAIAutomation)}
      hasBoxShadow={!lodgeAIAutomation?.isManualIntervention}
      variant={isInAutomation ? "gradient" : "outlined"}
      hasAnimation={isInAutomation}
      padding={0}
    >
      {!streamUrl ? (
        <Stack alignItems="center" justifyContent="center" gap={4} minHeight="768px">
          {hasLoadingTimedOut ? (
            <>
              <ErrorOutlineIcon sx={{ fontSize: "2rem", margin: "0 auto" }} />
              <Typography level="body-md">The automation frame is taking longer than expected.</Typography>
              <Button variant="secondary" onClick={handleClose}>
                Close
              </Button>
            </>
          ) : (
            <Loader text="Loading automation..." height="768px" />
          )}
        </Stack>
      ) : (
        <Stack position="relative" flexDirection="column" alignItems="flex-end">
          <StyledIFrame
            src={streamUrl}
            sandbox="allow-same-origin allow-scripts"
            allow="clipboard-read; clipboard-write"
            title="Automated Browser Session"
            data-manual={!isInAutomation}
          />
        </Stack>
      )}
    </Sheet>
  );
};

export { AutomationFrame };
